import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { auth, onAuthStateChanged, signOut, type User } from '../config/firebase'
import { supportApi } from '../services/api'

interface AuthState {
  user: User | null
  loading: boolean
  error: string | null
}

interface AuthContextValue extends AuthState {
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  })

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Register with backend to ensure user record exists
          await supportApi.registerWithBackend()
          setState({ user: firebaseUser, loading: false, error: null })
        } catch (err) {
          console.error('Backend registration failed:', err)
          // Still set the user — they're authenticated with Firebase
          setState({ user: firebaseUser, loading: false, error: null })
        }
      } else {
        setState({ user: null, loading: false, error: null })
      }
    })
    return unsubscribe
  }, [])

  const logout = async () => {
    await signOut(auth)
  }

  return (
    <AuthContext.Provider value={{ ...state, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
